import { scroll } from 'quasar'
import getScrollTarget = scroll.getScrollTarget
import setVerticalScrollPosition = scroll.setVerticalScrollPosition
import type { ComponentPublicInstance, Ref } from '@/composition/vue/compositionApi'

export const DEFAULT_SCROLL_DURATION = 1000

export const scrollToElement = (element: HTMLElement, duration: number = DEFAULT_SCROLL_DURATION): void => {
  const target = getScrollTarget(element)
  const offset = element.offsetTop

  setVerticalScrollPosition(target, offset, duration)
}

export const useScrollToComponent = (componentRef: Ref<ComponentPublicInstance | null>) => {
  const component = componentRef.value
  if (component) {
    scrollToElement(component.$el)
  }
}

type EasyFunctionOptions = {
  startPosition: number
  endPosition: number
  timeElapsed: number
  duration: number
}

const easeFunction = ({ startPosition, endPosition, timeElapsed, duration }: EasyFunctionOptions) => {
  const distance = endPosition - startPosition
  return startPosition + Math.max(distance * (timeElapsed / duration) ** 3, 1)
}

export const smoothScrollToCenterElementY = (el: HTMLElement, wrapper: HTMLElement, duration = 300) => {
  let offset = 0
  let parent = el.offsetParent as HTMLElement

  while (parent && wrapper.contains(parent) && parent !== wrapper) {
    offset += parent.offsetTop
    parent = parent.offsetParent as HTMLElement
  }

  const elOffsetTop = el.offsetTop + offset
  const elCenterY = elOffsetTop + el.offsetHeight / 2
  const targetScrollTop = elCenterY - wrapper.offsetHeight / 2
  const startPosition = wrapper.scrollTop

  let startTime: null | number = null

  const step = () => {
    if ('scrollBehavior' in document.documentElement.style) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' })
    } else {
      if (startTime === null) startTime = Date.now()

      const timeElapsed = Date.now() - startTime

      const scrollTop = easeFunction({
        startPosition,
        endPosition: targetScrollTop,
        timeElapsed,
        duration,
      })

      wrapper.scrollTop = scrollTop

      if (scrollTop >= targetScrollTop) {
        return
      }

      requestAnimationFrame(step)
    }
  }

  step()
}

export const hasVerticalScrollbar = (element: HTMLElement) => element.scrollHeight > element.clientHeight
export const hasHorizontalScrollbar = (element: HTMLElement) => element.scrollWidth > element.clientWidth
